import { CREATIVE_JOB_STATUS } from "./creativeJobStatus.js";
import { isExpectedUserError } from "./sentryReporting.js";

const claimedJobKeys = new Set();

const PRODUCE_JOB_TYPE = "produceCreativeTemplate";
const PRODUCE_CANCELLED_MESSAGE = "Produce cancelled";
const PRODUCE_FAILURE_MESSAGE = "Failed to produce template creatives";

const isAxiosError = (error) =>
  error.isAxiosError === true ||
  (Boolean(error.config) && Boolean(error.request));

const jobClaimKey = ({ advertiserID, jobID }) =>
  `${String(advertiserID || "")}:${String(jobID)}`;

// Job results are the plain { ok: false } payload from the RTDB wait, or a
// listener Error. Axios failures from createJob stay unmarked.
export const isCreativeJobResultError = (error) => {
  if (!error || typeof error !== "object") return false;
  if (isAxiosError(error)) return false;
  if (error.ok === false) return true;
  return error instanceof Error;
};

// True the first time this job is reported. LaunchError treats false as
// "already sent", so a second catch does not emit another Sentry event.
export const claimCreativeJobSentryReport = ({ advertiserID, jobID } = {}) => {
  if (!jobID) return false;

  const key = jobClaimKey({ advertiserID, jobID });
  if (claimedJobKeys.has(key)) return false;

  claimedJobKeys.add(key);
  return true;
};

export const resetCreativeJobSentryClaimsForTests = () => {
  claimedJobKeys.clear();
};

const readProduceSummary = (job) => {
  const summary = job?.response?.data?.summary;
  if (!summary || typeof summary !== "object") return null;
  return summary;
};

const readBackendMessage = (job) =>
  typeof job?.response?.message === "string" ? job.response.message.trim() : "";

const buildContext = (job) => {
  const payload = job?.payloadSnapshot || job?.payload || {};
  const data = job?.response?.data || {};
  const summary = readProduceSummary(job);
  const context = { jobID: job.jobID, jobType: job.jobType };
  const templateID = data.templateID || payload.templateID;
  const produceID = data.produceID || payload.produceID;
  if (templateID) context.templateID = templateID;
  if (produceID) context.produceID = produceID;
  if (payload.intent) context.intent = payload.intent;
  if (summary) context.summary = summary;
  return context;
};

// Null means the listener should not send this job to Sentry.
// Cancelled work and expected user errors stay out. A produce job that
// finishes "success" with failed cells is still a failure.
export const getCreativeJobSentryFailure = (job) => {
  if (!job?.jobID || job.cancelled === true) return null;
  if (job.status === CREATIVE_JOB_STATUS.CANCELLED) return null;

  const summary = readProduceSummary(job);
  const failed = Number(summary?.failed) || 0;
  const succeeded = Number(summary?.succeeded) || 0;
  const isProduce = job.jobType === PRODUCE_JOB_TYPE;
  const isJobError = job.status === CREATIVE_JOB_STATUS.ERROR;
  const isPartialFailure =
    isProduce && job.status === CREATIVE_JOB_STATUS.SUCCESS && failed > 0;
  if (!isJobError && !isPartialFailure) return null;

  const backendMessage = readBackendMessage(job);
  if (backendMessage === PRODUCE_CANCELLED_MESSAGE) return null;

  const code =
    job.response?.code ||
    (isPartialFailure ? "partialFailure" : "ERROR_OCCURRED");

  if (
    isExpectedUserError({
      code,
      message: backendMessage,
      cancelled: job.cancelled === true,
    })
  ) {
    return null;
  }

  const message =
    backendMessage ||
    (isPartialFailure
      ? `${PRODUCE_FAILURE_MESSAGE} (${failed} failed, ${succeeded} succeeded)`
      : `Creative job failed (${job.jobType || "unknown"})`);

  return {
    jobID: job.jobID,
    jobType: job.jobType,
    message,
    code,
    context: buildContext(job),
  };
};

// The first listener snapshot only records ids that are already failed.
// Later calls report jobs that newly moved to an error state.
export const selectUnreportedCreativeJobFailures = ({
  jobs,
  reportedIDs,
} = {}) => {
  if (!reportedIDs) return [];

  const next = [];
  for (const job of jobs || []) {
    const failure = getCreativeJobSentryFailure(job);
    if (!failure || reportedIDs.has(failure.jobID)) continue;
    next.push(job);
  }
  return next;
};
